import { Doc } from "@/components/Doc/Doc";
import { Icons } from "@/components/Icons/Icons";
import { dealModel, DealRecord } from "@/models/deal";
import {
  financialDeckModel,
  FinancialDeckRecord,
} from "@/models/financialDecks";
import { belongsToIds, Entity } from "@/orm/types";
import { Route } from "@/paths";
import { textFontFamily } from "@/theme";
import {
  currencies,
  getFallbackCurrency,
  setFallbackCurrency,
} from "@/utils/moneyFormating";
import {
  ActionIcon,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { useState } from "react";
import { BiPlus } from "react-icons/bi";
import { LabelExplainer } from "../LabelExplainer/LabelExplainer";
import { AddExistingDeal } from "./AddExistingDeal";
import { AddToNewDeal } from "./AddToNewDeal";

export const CreateFinancialDeckModal = ({
  dealEntity,
}: {
  dealEntity?: Entity<DealRecord>;
}) => {
  const [modelName, setModelName] = useState("");
  const [selectedDealType, setSelectedDealType] = useState("new");
  const [loanType, setLoanType] = useState<FinancialDeckRecord["loanType"]>(
    (localStorage.getItem(
      "lastSetLoanType",
    ) as FinancialDeckRecord["loanType"]) || "sba7a",
  );
  const [currencyCode, setCurrencyCode] = useState<
    FinancialDeckRecord["currencyCode"]
  >(getFallbackCurrency().code);

  const [newDealName, setNewDealName] = useState("");
  const [existingDeal, setExistingDeal] = useState<
    Entity<DealRecord> | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);

    if (!dealEntity) {
      if (selectedDealType === "new") {
        if (!newDealName) {
          setIsSubmitting(false);
          setErrorMessage("Please enter a deal name");
          return;
        }

        dealEntity = await dealModel.create({
          companyName: newDealName,
        });

        if (!dealEntity) {
          setIsSubmitting(false);
          setErrorMessage("Failed to create deal");
          return;
        }
      } else {
        if (!existingDeal) {
          setIsSubmitting(false);
          setErrorMessage("Please use the search to select an existing deal");
          return;
        }

        dealEntity = existingDeal;
      }
    }

    const deck = await financialDeckModel.create({
      name: modelName,
      loanType,
      currencyCode: loanType === "sba7a" ? "USD" : currencyCode,
      belongsToDealIds: belongsToIds<DealRecord>([dealEntity.id]),
    });

    if (!deck) {
      setIsSubmitting(false);
      setErrorMessage("Failed to create financial model");
      return;
    }

    localStorage.setItem("lastSetLoanType", loanType!);
    window.open(Route.Model.replace(":modelId", deck!.id), "_blank");
    modals.closeAll();
  };

  return (
    <Stack gap={dealEntity ? "lg" : "xs"}>
      <Group align="flex-start">
        <TextInput
          flex={1}
          label="Model Name:"
          placeholder="My Model Name"
          value={modelName}
          onChange={(e) => setModelName(e.currentTarget.value)}
        />

        <Stack gap={5}>
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Loan Type:
            </Text>
            {!dealEntity && (
              <Text size="xs" fw={500} lh={1}>
                <LabelExplainer
                  zIndex={1000}
                  showIcon
                  label={
                    loanType === "sba7a"
                      ? "Why choose a SBA (7a) Loan?"
                      : "Why choose a conventional loan?"
                  }
                  explanation={
                    <Doc
                      path={
                        loanType === "sba7a"
                          ? "@/docs/modals/create-new-model/why-choose-sb7a.mdx"
                          : "@/docs/modals/create-new-model/why-choose-conventional.mdx"
                      }
                    />
                  }
                />
              </Text>
            )}
          </Group>
          <SegmentedControl
            color="blue"
            value={loanType || "sba7a"}
            onChange={(val) =>
              setLoanType(val as FinancialDeckRecord["loanType"])
            }
            styles={{
              control: {
                width: 146,
              },
            }}
            data={[
              {
                label: " SBA (7a) Loan ",
                value: "sba7a" satisfies FinancialDeckRecord["loanType"],
              },
              {
                label: "Conventional Loan",
                value: "conventional" satisfies FinancialDeckRecord["loanType"],
              },
            ]}
          />
        </Stack>
        <Select
          style={{
            opacity: loanType === "sba7a" ? 0 : 1,
            pointerEvents: loanType === "sba7a" ? "none" : "auto",
          }}
          label="Currency:"
          withCheckIcon={false}
          size="sm"
          w={98}
          styles={{
            option: { textAlign: "center", fontFamily: textFontFamily },
            input: { textAlign: "center", fontFamily: textFontFamily },
          }}
          data={currencies.map((currency) => ({
            value: currency.code,
            label: currency.shortLabel,
          }))}
          value={currencyCode}
          onChange={(value) => {
            setCurrencyCode(value as FinancialDeckRecord["currencyCode"]);
            setFallbackCurrency(value as FinancialDeckRecord["currencyCode"]);
          }}
        />
      </Group>

      {dealEntity && (
        <Text size="md">
          {loanType == "sba7a" && (
            <>
              SBA 7(a) loans are ideal for U.S.-based acquisitions in the $3-6M
              range with government-backed financing.
            </>
          )}
          {loanType == "conventional" && (
            <>
              Conventional loans are best for larger acquisitions or
              international deals—they offer more flexible financing terms but
              generally require higher equity injections.
            </>
          )}{" "}
          <LabelExplainer
            inline
            zIndex={1000}
            showIcon
            label=" Learn More"
            explanation={
              <Doc
                path={
                  loanType === "sba7a"
                    ? "@/docs/modals/create-new-model/why-choose-sb7a.mdx"
                    : "@/docs/modals/create-new-model/why-choose-conventional.mdx"
                }
              />
            }
          />
        </Text>
      )}

      {!dealEntity && (
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Deal:
          </Text>
          <Card withBorder bg={"gray.1"} p="xs">
            <Stack gap={6}>
              <SegmentedControl
                autoContrast
                color="blue"
                value={selectedDealType}
                onChange={setSelectedDealType}
                data={[
                  { label: "Add To New Deal", value: "new" },
                  { label: "Add To Existing Deal", value: "existing" },
                ]}
                bg={"white"}
                bd={"1px solid #E0E0E0"}
              />
              <Text c="dimmed" size="sm" ta={"center"}>
                {selectedDealType === "new"
                  ? "Create a deal with the new model and add it to the deal tracker."
                  : "Choose an existing deal and create a new model for it."}
              </Text>

              {selectedDealType === "new" ? (
                <AddToNewDeal
                  value={newDealName}
                  onChange={(value) => setNewDealName(value)}
                />
              ) : (
                <>
                  {!existingDeal && (
                    <AddExistingDeal
                      onSelect={(deal) => setExistingDeal(deal)}
                    />
                  )}
                  {existingDeal && (
                    <Card withBorder fw={"bold"}>
                      <Group justify="space-between">
                        {existingDeal?.data?.companyName}
                        <ActionIcon onClick={() => setExistingDeal(undefined)}>
                          <Icons.Remove />
                        </ActionIcon>
                      </Group>
                    </Card>
                  )}
                </>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
      <Group justify="center">
        <Button
          leftSection={<BiPlus size={16} />}
          loading={isSubmitting}
          onClick={onSubmit}
          w={200}
        >
          Create Model
        </Button>
      </Group>
      {errorMessage && (
        <Text c="red" ta="center">
          {errorMessage}
        </Text>
      )}
    </Stack>
  );
};
